import { User } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";

export type TAvatarProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src?: string;
  alt?: string;
  size?: "sm" | "default" | "lg";
  fallback?: React.ReactNode;
  name?: string;
};

const sizeClasses = {
  sm: "h-6 w-6",
  default: "h-8 w-8",
  lg: "h-10 w-10",
};

const AVATAR_COLORS = [
  "#E53935", // red
  "#8E24AA", // purple
  "#1E88E5", // blue
  "#00897B", // teal
  "#43A047", // green
  "#FB8C00", // orange
  "#6D4C41", // brown
  "#00ACC1", // cyan
  "#F4511E", // deep orange
  "#3949AB", // indigo
];

function getColorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export const Avatar = React.forwardRef<HTMLImageElement, TAvatarProps>(
  ({ src, alt = "User avatar", size = "default", className, fallback, name, ...props }, ref) => {
    const [imgError, setImgError] = React.useState(false);

    const sizeClass = sizeClasses[size];

    if (!src || imgError) {
      const initial = name?.[0]?.toUpperCase();
      const bgColor = name ? getColorForName(name) : undefined;

      return (
        <div
          className={cn("flex items-center justify-center rounded-full", sizeClass, className)}
          style={bgColor ? { backgroundColor: bgColor } : undefined}
          {...(props as any)}
        >
          {fallback ?? (
            initial ? (
              <span
                className={cn(
                  "font-semibold text-white",
                  size === "sm" && "text-xs",
                  size === "default" && "text-sm",
                  size === "lg" && "text-base"
                )}
              >
                {initial}
              </span>
            ) : (
              <div className={cn("flex items-center justify-center rounded-full bg-muted text-muted-foreground", sizeClass)}>
                <User className={cn("h-4 w-4", size === "lg" && "h-5 w-5", size === "sm" && "h-3 w-3")} />
              </div>
            )
          )}
        </div>
      );
    }

    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={cn("rounded-full object-cover", sizeClass, className)}
        onError={() => setImgError(true)}
        {...props}
      />
    );
  }
);

Avatar.displayName = "Avatar";
